import type { ColDef, GetQuickFilterTextParams, KeyCreatorParams, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import { ChipCellRenderer } from "../cell-renderers/ChipCellRenderer";
import { CommentCellRenderer } from "../cell-renderers/CommentCellRenderer";
import { DateCellRenderer } from "../cell-renderers/DateCellRenderer";
import { DollarCurrencyCellRenderer } from "../cell-renderers/DollarCurrencyCellRenderer";
import { LinkCellRenderer } from "../cell-renderers/LinkCellRenderer";
import { PaymentStatusCellRenderer } from "../cell-renderers/PaymentStatusCellRenderer";
import { PercentageCellRenderer } from "../cell-renderers/PercentageCellRenderer";
import { StatusCellRenderer } from "../cell-renderers/StatusCellRenderer";
import { TagsCellRenderer } from "../cell-renderers/TagsCellRenderer";
import { UserCellRenderer } from "../cell-renderers/UserCellRenderer";
import { VolumeCellRenderer, volumeComparator } from "../cell-renderers/VolumeCellRenderer";

import { extractNumericValue, getNumericColumnConfig } from "./cell-renderer-utils";
import { convertISODateToDateObject, fDate } from "./grid-helpers";

export const CELL_RENDERER_REGISTRY = {
  LINK: LinkCellRenderer,
  USER: UserCellRenderer,
  TAGS: TagsCellRenderer,
  COMMENT: CommentCellRenderer,
  CHIP: ChipCellRenderer,
  STATUS: StatusCellRenderer,
  PAYMENT_STATUS: PaymentStatusCellRenderer,
  VOLUME: VolumeCellRenderer,
  PERCENTAGE: PercentageCellRenderer,
  DOLLAR_CURRENCY: DollarCurrencyCellRenderer,
  DATE_RENDERER: DateCellRenderer,
};

export const getCellRendererComponentFromMetadata = (type: string, column: ColDef): ColDef => {
  const typeUpper = type?.toUpperCase?.() || type;

  switch (typeUpper) {
    case "LINK":
      return {
        valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        cellRenderer: CELL_RENDERER_REGISTRY.LINK,
        filterParams: {
          buttons: ["reset", "apply"],
          newRowsAction: "keep",
          suppressAndOrCondition: true,
          closeOnApply: true,
        },
      };
    case "PAYMENT_STATUS":
      return {
        cellRenderer: CELL_RENDERER_REGISTRY.PAYMENT_STATUS,
        filter: false,
        sortable: false,
      };
    case "USER":
      return {
        valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        cellRenderer: CELL_RENDERER_REGISTRY.USER,
        filterParams: {
          buttons: ["reset", "apply"],
          newRowsAction: "keep",
          suppressAndOrCondition: true,
          closeOnApply: true,
        },
      };
    case "TAGS":
      return {
        cellRenderer: CELL_RENDERER_REGISTRY.TAGS,
        valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        filterParams: {
          buttons: ["reset", "apply"],
          newRowsAction: "keep",
          suppressAndOrCondition: true,
          closeOnApply: true,
        },
      };
    case "COMMENT":
      return {
        filter: "agTextColumnFilter",
        valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        cellRenderer: CELL_RENDERER_REGISTRY.COMMENT,
        filterParams: {
          buttons: ["reset", "apply"],
          newRowsAction: "keep",
          suppressAndOrCondition: true,
          closeOnApply: true,
        },
      };
    case "CHIP":
      return {
        valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        cellRenderer: CELL_RENDERER_REGISTRY.CHIP,
        filterParams: {
          buttons: ["reset", "apply"],
          newRowsAction: "keep",
          suppressAndOrCondition: true,
          closeOnApply: true,
          keyCreator: (params: KeyCreatorParams) => (params?.value as string) ?? "",
          valueFormatter: (p: ValueFormatterParams) => (p.value as string) || "",
        },
      };
    case "STATUS":
      return {
        cellRenderer: CELL_RENDERER_REGISTRY.STATUS,
        filter: "agSetColumnFilter",
        filterParams: {
          buttons: ["reset", "apply"],
          closeOnApply: true,
          suppressRemoveEntries: false,
          suppressSelectAll: false,
          newRowsAction: "keep",
        },
      };
    case "VOLUME":
      return {
        valueGetter: (params: ValueGetterParams) => {
          const colId = params.column.getColId();
          const cellData = params.data?.[colId];
          return extractNumericValue(cellData);
        },
        cellRenderer: CELL_RENDERER_REGISTRY.VOLUME,
        comparator: volumeComparator,
        ...getNumericColumnConfig(),
      };
    case "PERCENTAGE":
      return {
        valueGetter: (params: ValueGetterParams) => {
          const colId = params.column.getColId();
          const cellData = params.data?.[colId];
          return extractNumericValue(cellData);
        },
        cellRenderer: CELL_RENDERER_REGISTRY.PERCENTAGE,
        ...getNumericColumnConfig(),
      };
    case "DOLLAR_CURRENCY":
      return {
        valueGetter: (params: ValueGetterParams) => {
          const colId = params.column.getColId();
          const cellData = params.data?.[colId];
          return extractNumericValue(cellData);
        },
        cellRenderer: CELL_RENDERER_REGISTRY.DOLLAR_CURRENCY,
        ...getNumericColumnConfig(),
      };
    case "DATE_RENDERER":
      return {
        cellRenderer: CELL_RENDERER_REGISTRY.DATE_RENDERER,
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            const dateAsString = cellValue;
            const cellDate = convertISODateToDateObject(dateAsString);
            if (dateAsString == null || cellDate == null) {
              return "";
            }

            if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
              return 0;
            }
            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            }
            if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            }
            return 0;
          },
          buttons: ["reset", "apply"],
          maxNumConditions: 1,
          inRangeInclusive: true,
          closeOnApply: true,
        },
        getQuickFilterText: (params: GetQuickFilterTextParams) => fDate(params?.value),
      };
    default:
      return column;
  }
};
